import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { useInvitations } from '../context/InvitationContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Mail, Check, X, Briefcase, Film as FilmIcon } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';

const API = API_BASE;

const FILM_ROLES = {
  director: 'Director',
  producer: 'Producer',
  cinematographer: 'Cinematographer',
  editor: 'Editor',
  music_director: 'Music Director',
  actor: 'Actor/Actress',
  crew: 'Crew',
  production_assistant: 'Production Assistant',
  writer: 'Writer'
};

const ACCESS_TYPES = {
  admin: { label: 'Admin', color: 'bg-primary/20 text-primary' },
  editor: { label: 'Editor', color: 'bg-success/15 text-success' },
  viewer: { label: 'Viewer', color: 'bg-muted text-muted-foreground' }
};

const WORKSPACE_ROLE_LABELS = {
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer'
};
const WORKSPACE_ROLE_STYLES = {
  admin: 'bg-primary/20 text-primary',
  member: 'bg-success/15 text-success',
  viewer: 'bg-muted text-muted-foreground'
};

const Invitations = () => {
  const navigate = useNavigate();
  const {
    invitations,                // film
    workspaceInvitations,
    loading,
    refreshInvitations,
  } = useInvitations();

  // ---------- film invitation handlers (existing flow) ----------
  const handleAcceptFilm = async (invitationId) => {
    try {
      await axios.post(`${API}/invitations/${invitationId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('Invitation accepted! You can now access the film.');
      refreshInvitations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to accept invitation';
      toast.error(errorMsg);
    }
  };

  const handleDeclineFilm = async (invitationId) => {
    try {
      await axios.delete(`${API}/invitations/${invitationId}`, { headers: getAuthHeaders() });
      toast.success('Invitation declined');
      refreshInvitations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to decline invitation';
      toast.error(errorMsg);
    }
  };

  // ---------- workspace invitation handlers (new flow) ----------
  const handleAcceptWorkspace = async (invitationId) => {
    try {
      await axios.post(
        `${API}/workspaces/invitations/${invitationId}/accept`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Joined workspace! It now appears in your workspace list.');
      refreshInvitations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to accept invitation';
      toast.error(errorMsg);
    }
  };

  const handleDeclineWorkspace = async (invitationId) => {
    try {
      await axios.post(
        `${API}/workspaces/invitations/${invitationId}/decline`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Invitation declined');
      refreshInvitations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to decline invitation';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const totalCount = invitations.length + workspaceInvitations.length;

  return (
    <div className="bg-background" data-testid="invitations-page">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-3xl">
        <div className="flex items-center gap-2 md:gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            data-testid="back-button"
            className="-ml-2 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">Invitations</h1>
            <p className="text-xs md:text-sm text-muted-foreground">
              {totalCount} pending invitation{totalCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {totalCount === 0 ? (
          <Card className="p-12 text-center">
            <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No pending invitations</h3>
            <p className="text-muted-foreground">
              When someone invites you to a workspace or a film, it will appear here.
            </p>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Workspace invitations */}
            {workspaceInvitations.length > 0 && (
              <section data-testid="workspace-invitations-section">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                    Workspace invitations · {workspaceInvitations.length}
                  </h2>
                </div>
                <div className="space-y-4">
                  {workspaceInvitations.map((inv) => (
                    <Card key={inv.id} className="p-6" data-testid={`workspace-invitation-${inv.id}`}>
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-semibold font-[Manrope] mb-1">
                            {inv.workspace_name || 'Workspace'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Invited by {inv.inviter_name || 'a teammate'}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <span
                            className={`text-xs px-3 py-1 rounded-sm font-medium ${WORKSPACE_ROLE_STYLES[inv.role] || 'bg-muted text-muted-foreground'}`}
                          >
                            {WORKSPACE_ROLE_LABELS[inv.role] || inv.role}
                          </span>
                          <span className="text-xs px-3 py-1 rounded-sm bg-secondary text-muted-foreground border border-border">
                            Workspace
                          </span>
                        </div>

                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleAcceptWorkspace(inv.id)}
                            className="flex-1"
                            data-testid={`accept-workspace-${inv.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Accept
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDeclineWorkspace(inv.id)}
                            className="flex-1"
                            data-testid={`decline-workspace-${inv.id}`}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Decline
                          </Button>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Invited {new Date(inv.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Film invitations */}
            {invitations.length > 0 && (
              <section data-testid="film-invitations-section">
                <div className="flex items-center gap-2 mb-3">
                  <FilmIcon className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                    Film invitations · {invitations.length}
                  </h2>
                </div>
                <div className="space-y-4">
                  {invitations.map((invitation) => (
                    <Card key={invitation.id} className="p-6" data-testid={`invitation-${invitation.id}`}>
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-lg font-semibold font-[Manrope] mb-1">
                            {invitation.film_title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Invited by {invitation.invited_by_name}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-3 py-1 rounded-sm bg-secondary text-foreground border border-border">
                            {FILM_ROLES[invitation.role] || invitation.role}
                          </span>
                          {invitation.access_type && (
                            <span className={`text-xs px-3 py-1 rounded-sm font-medium ${ACCESS_TYPES[invitation.access_type]?.color || 'bg-muted text-muted-foreground'}`}>
                              {ACCESS_TYPES[invitation.access_type]?.label || invitation.access_type}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleAcceptFilm(invitation.id)}
                            className="flex-1"
                            data-testid={`accept-${invitation.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Accept
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDeclineFilm(invitation.id)}
                            className="flex-1"
                            data-testid={`decline-${invitation.id}`}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Decline
                          </Button>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Invited {new Date(invitation.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Invitations;
